import { mutate } from 'swr';
import { doc, collection, setDoc as _setDoc, updateDoc as _updateDoc, deleteDoc as _deleteDoc } from '@firebase/firestore';
import { fuego } from '../context/Provider';
import { empty } from '../helpers/empty';
import { collectionCache } from '../classes/Cache';

const shouldMerge = options => {
  return !!(options !== null && options !== void 0 && options.merge);
};
/**
 * Function that, when called, refreshes all queries that match this document path.
 *
 * This can be useful for a pull to refresh that isn't on the same screen as the `useCollection` hook, for example.
 */


const revalidateDoc = path => {
  return mutate(path);
};
/**
 * Function that, when called, refreshes all queries that match this document path.
 *
 * This can be useful for a pull to refresh that isn't on the same screen as the `useCollection` hook, for example.
 */


const revalidateCollection = path => {
  const promises = [];
  collectionCache.getSWRKeysFromCollectionPath(path).forEach(key => {
    promises.push(mutate(key));
  });
  return Promise.all(promises);
};

const setDoc = function (path, data) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let ignoreLocalMutation = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  if (path === null) return null;
  const isDocument = path.trim().split('/').filter(Boolean).length % 2 === 0;

  if (!ignoreLocalMutation) {
    mutate(path, function () {
      let prevState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.object;
      if (shouldMerge(options)) return data;
      return { ...prevState,
        ...data
      };
    }, false);
  }

  const ref = isDocument ? doc(fuego.db, path) : doc(collection(fuego.db, path));
  collectionCache.getSWRKeysFromCollectionPath(ref.parent.path).forEach(key => {
    mutate(key, function () {
      let currentState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.array;

      // don't mutate the current state if it doesn't include this doc
      // why? to prevent creating a new reference of the state
      // creating a new reference could trigger unnecessary re-renders
      if (!currentState.some(doc => doc.id === ref.id)) {
        return currentState;
      }

      return currentState.map(function () {
        let document = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.object;

        if (document.id === ref.id) {
          if (!shouldMerge(options)) return document;
          return { ...document,
            ...data
          };
        }

        return document;
      });
    }, false);
  });
  return _setDoc(ref, data, options);
};

const updateDoc = function (path, data) {
  let ignoreLocalMutation = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (path === null) return null;
  const isDocument = path.trim().split('/').filter(Boolean).length % 2 === 0;
  if (!isDocument) throw new Error(`[@nandorojo/swr-firestore] error: called update function with path: ${path}. This is not a valid document path. 
      
data: ${JSON.stringify(data)}`);

  if (!ignoreLocalMutation) {
    mutate(path, function () {
      let prevState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.object;
      return { ...prevState,
        ...data
      };
    }, false);
  }

  let collection = path.split(`/`).filter(Boolean);
  const docId = collection.pop(); // remove last item, which is the /doc-id

  collection = collection.join('/');
  collectionCache.getSWRKeysFromCollectionPath(collection).forEach(key => {
    mutate(key, function () {
      let currentState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.array;

      // don't mutate the current state if it doesn't include this doc
      if (!currentState.some(doc => doc.id === docId)) {
        return currentState;
      }

      return currentState.map(function () {
        let document = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.object;

        if (document.id === docId) {
          return { ...document,
            ...data
          };
        }

        return document;
      });
    }, false);
  });
  return _updateDoc(doc(fuego.db, path), data);
};

const deleteDoc = function (path) {
  let ignoreLocalMutation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  if (path === null) return null;
  const isDocument = path.trim().split('/').filter(Boolean).length % 2 === 0;
  if (!isDocument) throw new Error(`[@nandorojo/swr-firestore] error: called delete() function with path: ${path}. This is not a valid document path.`);

  if (!ignoreLocalMutation) {
    mutate(path, null, false);
    let collection = path.split(`/`).filter(Boolean);
    const docId = collection.pop(); // remove last item, which is the /doc-id

    collection = collection.join('/');
    collectionCache.getSWRKeysFromCollectionPath(collection).forEach(key => {
      mutate(key, function () {
        let currentState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.array;

        // don't mutate the current state if it doesn't include this doc
        // why? to prevent creating a new reference of the state
        // creating a new reference could trigger unnecessary re-renders
        if (!currentState.some(doc => doc && doc.id === docId)) {
          return currentState;
        }

        return currentState.filter(document => {
          if (!document) return false;

          if (document.id === docId) {
            // delete this doc
            return false;
          }

          return true;
        });
      }, false);
    });
  }

  return _deleteDoc(doc(fuego.db, path));
};

export { setDoc, updateDoc, deleteDoc, revalidateDoc, revalidateCollection, shouldMerge };
//# sourceMappingURL=static-mutations.js.map